// src/admin/AdminProSignups.jsx
//
// Leads captured from the JiraniPro landing page's signup form
// (pro_signups table). These are NOT pro_profiles — no auth
// account, no onboarding, nothing verified yet. This page is
// purely for triaging: see who signed up, call them, log a note,
// move them through pending -> contacted -> onboarded (or declined).
//
// Realtime: subscribes to postgres_changes on pro_signups so new
// signups appear the instant someone submits the form, no refresh
// needed. Relies on the pro_signups_admin_all RLS policy (requires
// profiles.role = 'admin' for the logged-in user) already granting
// SELECT/UPDATE — same auth context the rest of /admin uses.
//
// Status values assumed: pending | contacted | onboarded | declined.
// The column is plain text with no CHECK constraint, so if you use
// different values elsewhere, just edit STATUS_OPTIONS below.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "src/lib/supabase";

const STATUS_OPTIONS = [
  { value: "pending",   label: "New",       color: "#B45309", bg: "#FEF3C7" },
  { value: "contacted", label: "Contacted", color: "#1D4ED8", bg: "#DBEAFE" },
  { value: "onboarded", label: "Onboarded", color: "#15803D", bg: "#DCFCE7" },
  { value: "declined",  label: "Declined",  color: "#B91C1C", bg: "#FEE2E2" },
];
const statusMeta = (v) => STATUS_OPTIONS.find(s => s.value === v) || STATUS_OPTIONS[0];

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-KE", { day: "numeric", month: "short" });
}

const F = "'Plus Jakarta Sans', sans-serif";

function StatusBadge({ status, onChange }) {
  const [open, setOpen] = useState(false);
  const meta = statusMeta(status);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 999,
          background: meta.bg, color: meta.color, border: "none", fontSize: 12.5, fontWeight: 700,
          cursor: "pointer", fontFamily: F,
        }}
      >
        {meta.label}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={meta.color} strokeWidth="3"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 20, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, padding: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", minWidth: 140 }}>
          {STATUS_OPTIONS.map(o => (
            <div key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
              style={{ padding: "7px 10px", borderRadius: 6, fontSize: 13, fontWeight: 600, color: "#334155", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = "#F1F5F9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: o.color }} />
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KpiStrip({ leads, activeStatus, onClick }) {
  const counts = useMemo(() => {
    const c = { all: leads.length };
    STATUS_OPTIONS.forEach(o => { c[o.value] = leads.filter(l => l.status === o.value).length; });
    return c;
  }, [leads]);
  const cards = [
    { key: "all", label: "All signups", value: counts.all },
    { key: "pending", label: "New", value: counts.pending || 0 },
    { key: "contacted", label: "Contacted", value: counts.contacted || 0 },
    { key: "onboarded", label: "Onboarded", value: counts.onboarded || 0 },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
      {cards.map(c => (
        <div key={c.key} onClick={() => onClick(c.key)} style={{
          background: "#fff", border: `1px solid ${activeStatus === c.key ? "#0F172A" : "#E2E8F0"}`,
          borderRadius: 12, padding: "16px 18px", cursor: "pointer",
        }}>
          <p style={{ margin: "0 0 6px", fontSize: 12.5, fontWeight: 600, color: "#64748B", fontFamily: F }}>{c.label}</p>
          <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0F172A", fontFamily: F }}>{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function NotesCell({ lead, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(lead.admin_notes || "");
  useEffect(() => { setValue(lead.admin_notes || ""); }, [lead.admin_notes]);

  if (!editing) {
    return (
      <div onClick={() => setEditing(true)} style={{ cursor: "pointer", minWidth: 160, maxWidth: 220 }}>
        {lead.admin_notes ? (
          <p style={{ margin: 0, fontSize: 13, color: "#334155", fontFamily: F, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lead.admin_notes}</p>
        ) : (
          <span style={{ fontSize: 13, color: "#94A3B8", fontFamily: F }}>+ Add note</span>
        )}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "flex-start", minWidth: 220 }}>
      <textarea
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder="Call notes..."
        rows={2}
        style={{ flex: 1, fontSize: 13, fontFamily: F, border: "1px solid #CBD5E1", borderRadius: 6, padding: "6px 8px", resize: "vertical", outline: "none" }}
      />
      <button
        onClick={() => { onSave(value); setEditing(false); }}
        style={{ background: "#0F172A", color: "#fff", border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: F }}
      >Save</button>
    </div>
  );
}

export default function AdminProSignups() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeStatus, setActiveStatus] = useState("all");
  const [toast, setToast] = useState(null);
  const searchTimer = useRef(null);

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from("pro_signups")
        .select("id, name, phone, trade, area, status, admin_notes, created_at")
        .order("created_at", { ascending: false });
      if (fetchErr) throw fetchErr;
      setLeads(data || []);
    } catch (err) {
      console.error("Failed to load pro_signups:", err);
      setError(err.message || "Failed to load signups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  // Realtime: new signups land in the table immediately, and status
  // or note changes made elsewhere (another admin, SQL editor, etc.)
  // stay in sync without a refresh.
  useEffect(() => {
    const channel = supabase
      .channel("pro_signups_admin")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "pro_signups" }, (payload) => {
        setLeads(prev => prev.some(l => l.id === payload.new.id) ? prev : [payload.new, ...prev]);
        showToast(`New signup: ${payload.new.name}`, "info");
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "pro_signups" }, (payload) => {
        setLeads(prev => prev.map(l => l.id === payload.new.id ? payload.new : l));
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "pro_signups" }, (payload) => {
        setLeads(prev => prev.filter(l => l.id !== payload.old.id));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [showToast]);

  const handleSearchInput = useCallback((val) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearchQuery(val), 250);
  }, []);

  const filtered = useMemo(() => {
    let result = leads;
    if (activeStatus !== "all") result = result.filter(l => l.status === activeStatus);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l => `${l.name} ${l.phone} ${l.trade} ${l.area}`.toLowerCase().includes(q));
    }
    return result;
  }, [leads, activeStatus, searchQuery]);

  const updateStatus = useCallback(async (id, status) => {
    const prev = leads;
    setLeads(cur => cur.map(l => l.id === id ? { ...l, status } : l));
    try {
      const { error: updErr } = await supabase.from("pro_signups").update({ status }).eq("id", id);
      if (updErr) throw updErr;
      showToast("Status updated", "success");
    } catch (err) {
      console.error("Status update failed:", err);
      setLeads(prev);
      showToast(`Failed to update status: ${err.message}`, "error");
    }
  }, [leads, showToast]);

  const updateNotes = useCallback(async (id, admin_notes) => {
    const prev = leads;
    setLeads(cur => cur.map(l => l.id === id ? { ...l, admin_notes } : l));
    try {
      const { error: updErr } = await supabase.from("pro_signups").update({ admin_notes }).eq("id", id);
      if (updErr) throw updErr;
      showToast("Note saved", "success");
    } catch (err) {
      console.error("Note update failed:", err);
      setLeads(prev);
      showToast(`Failed to save note: ${err.message}`, "error");
    }
  }, [leads, showToast]);

  const th = { textAlign: "left", padding: "10px 16px", fontSize: 11.5, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: F, borderBottom: "1px solid #E2E8F0" };
  const td = { padding: "12px 16px", fontSize: 13.5, color: "#334155", fontFamily: F, borderBottom: "1px solid #F1F5F9", verticalAlign: "top" };

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#0F172A", fontFamily: F }}>Pro Signups</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748B", fontFamily: F }}>Leads from the JiraniPro landing page, ahead of full onboarding.</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", borderRadius: 10, padding: "8px 14px", border: "1px solid #E2E8F0", width: 300 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2"><circle cx="10" cy="10" r="7" /><line x1="21" y1="21" x2="15" y2="15" /></svg>
          <input
            type="text"
            placeholder="Search by name, phone, trade, area..."
            onChange={(e) => handleSearchInput(e.target.value)}
            style={{ border: "none", outline: "none", fontSize: 13.5, color: "#334155", width: "100%", background: "transparent", fontFamily: F }}
          />
        </div>
      </div>

      <KpiStrip leads={leads} activeStatus={activeStatus} onClick={setActiveStatus} />

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", fontSize: 13.5, color: "#64748B", fontFamily: F }}>Loading signups...</div>
        ) : error ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <p style={{ fontSize: 13.5, color: "#DC2626", fontFamily: F, marginBottom: 12 }}>{error}</p>
            <button onClick={loadLeads} style={{ background: "#0F172A", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: F }}>Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", fontSize: 13.5, color: "#64748B", fontFamily: F }}>No signups match this view.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Name</th>
                  <th style={th}>Phone</th>
                  <th style={th}>Trade</th>
                  <th style={th}>Area</th>
                  <th style={th}>Status</th>
                  <th style={th}>Notes</th>
                  <th style={th}>Signed up</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(lead => (
                  <tr key={lead.id}>
                    <td style={{ ...td, fontWeight: 600, color: "#0F172A" }}>{lead.name}</td>
                    <td style={td}>
                      <a href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" style={{ color: "#1D4ED8", textDecoration: "none" }}>{lead.phone}</a>
                    </td>
                    <td style={td}>{lead.trade}</td>
                    <td style={td}>{lead.area}</td>
                    <td style={td}><StatusBadge status={lead.status} onChange={(v) => updateStatus(lead.id, v)} /></td>
                    <td style={td}><NotesCell lead={lead} onSave={(v) => updateNotes(lead.id, v)} /></td>
                    <td style={{ ...td, color: "#64748B", whiteSpace: "nowrap" }}>{timeAgo(lead.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24,
          background: toast.type === "success" ? "#0F172A" : toast.type === "info" ? "#3B82F6" : "#DC2626",
          color: "#fff", padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600,
          zIndex: 999, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", fontFamily: F,
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}